import { CheckIcon, MagnifyingGlass, User, X } from '@phosphor-icons/react';
import { Button, Modal, Spinner, TextInput } from 'flowbite-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PLATFORM_ICON_MAP } from '../../../../shared/constants';
import type { IAccountsEntity } from '../interface';

interface AccountSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    platform: 'google_ads' | 'google_admob' | 'meta_ads' | 'apple_search_ads';
    accounts: IAccountsEntity[];
    selectedAccountId: string | null;
    onSelectAccount: (accountId: string) => void;
    onConfirm: () => void;
    isLoading: boolean;
    isConfirming?: boolean;
}

const AccountSelectionModal: React.FC<AccountSelectionModalProps> = ({
    isOpen,
    onClose,
    platform,
    accounts,
    selectedAccountId,
    onSelectAccount,
    onConfirm,
    isLoading,
    isConfirming = false,
}) => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');

    const platformConfig = {
        google_ads: {
            name: 'Google Ads',
            icon: PLATFORM_ICON_MAP.google_ads,
        },
        google_admob: {
            name: 'Google AdMob',
            icon: PLATFORM_ICON_MAP.google_admob,
        },
        meta_ads: {
            name: 'Meta Ads',
            icon: PLATFORM_ICON_MAP.facebook,
        },
        apple_search_ads: {
            name: 'Apple Search Ads',
            icon: PLATFORM_ICON_MAP.apple_ads,
        },
    };

    const config = platformConfig[platform];

    const filteredAccounts = useMemo(() => {
        if (!searchQuery.trim()) return accounts;
        const query = searchQuery.toLowerCase();
        return accounts.filter(
            (account) =>
                account.name?.toLowerCase().includes(query) ||
                account.id?.toLowerCase().includes(query)
        );
    }, [accounts, searchQuery]);

    return (
        <Modal show={isOpen} onClose={onClose} size="md" popup>
            <div className="relative bg-white rounded-2xl p-6">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <X size={20} weight="bold" />
                </button>

                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <img
                        src={config.icon}
                        alt={config.name}
                        className="w-10 h-10 rounded-lg"
                    />
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            {config.name}
                        </h3>
                        <p className="text-sm text-gray-500">{t('configurations.integrations.selectAccountToConnect')}</p>
                    </div>
                </div>

                {/* Search Input */}
                <div className="mb-4">
                    <TextInput
                        icon={MagnifyingGlass}
                        placeholder={t('configurations.integrations.searchAccounts')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full"
                    />
                </div>

                {/* Accounts List */}
                <div className="max-h-64 overflow-y-auto space-y-2 mb-4">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <Spinner size="lg" color="purple" />
                        </div>
                    ) : filteredAccounts.length > 0 ? (
                        filteredAccounts.map((account) => {
                            const accountId = account.id || account.publisher_id || '';
                            const isSelected = String(selectedAccountId) === String(accountId);

                            return (
                                <button
                                    key={accountId}
                                    onClick={() => onSelectAccount(accountId)}
                                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${isSelected
                                        ? 'border-emerald-500 bg-emerald-50'
                                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center ${isSelected ? 'bg-emerald-500' : 'bg-gray-100'
                                            }`}
                                    >
                                        <User size={14} weight="bold" className={isSelected ? "text-white" : "text-gray-400"} />

                                    </div>
                                    <div className="flex-1 text-left">
                                        <p className="text-sm font-semibold text-gray-800">
                                            {platform !== 'google_admob' ? `${account.name} (${accountId})` : accountId}
                                        </p>
                                        {account.currency_code && (
                                            <p className="text-xs text-gray-500">
                                                {account.currency_code}
                                            </p>
                                        )}
                                    </div>
                                    <div className={`w-6 h-6 rounded-full ${isSelected ? 'bg-emerald-500' : 'bg-white border border-gray-300'} flex items-center justify-center`}>
                                        <CheckIcon size={12} weight="bold" className={isSelected ? "text-white" : "text-gray-300"} />
                                    </div>
                                </button>
                            );
                        })
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            {searchQuery ? t('configurations.integrations.noAccountsMatch') : t('configurations.integrations.noAccounts')}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end pt-4 border-t border-gray-100">
                    <Button
                        color="success"
                        onClick={onConfirm}
                        disabled={!selectedAccountId || isConfirming}
                        isProcessing={isConfirming}
                    >
                        {t('configurations.integrations.confirmSelection')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default AccountSelectionModal;
